import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Symbol & Icon Templates — 12 templates
   lightning_bolt, light_bulb, gear_mechanism, arrow_cycle,
   people_group, clock_face, shield_badge, magnifying_glass,
   target_bullseye, key_lock, flag_banner, trophy_cup
   ═══════════════════════════════════════════════════════════════ */

const symGrad = (id: string, c1: string, c2: string) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={c1} />
    <stop offset="100%" stopColor={c2} />
  </linearGradient>
);

/* ─── 1. Lightning Bolt ───────────────────────────────────── */
export const LightningBolt: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Energy";
  const accentColor = (props.accentColor as string) || "#f1c40f";
  const opacity = (props.opacity as number) ?? 1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%" opacity={opacity}>
      <defs>
        {symGrad("lb-bg", "#1a1a2e", "#0d0d1a")}
        <filter id="lb-glow">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <rect width="800" height="600" fill="url(#lb-bg)" rx="16" />
      {/* Cloud */}
      <g opacity="0.5">
        <ellipse cx="350" cy="120" rx="100" ry="45" fill="#34495e" />
        <ellipse cx="420" cy="105" rx="80" ry="40" fill="#3d566e" />
        <ellipse cx="300" cy="110" rx="70" ry="35" fill="#3d566e" />
      </g>
      {/* Lightning bolt */}
      <g filter="url(#lb-glow)">
        <polygon points="420,140 340,300 400,300 330,480 480,260 410,260 480,140"
          fill={accentColor} stroke="#fff" strokeWidth="2" />
      </g>
      {/* Glow circle */}
      <circle cx="400" cy="300" r="160" fill={accentColor} opacity="0.06" />
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Light Bulb ───────────────────────────────────────── */
export const LightBulb: React.FC<Record<string, unknown>> = (props) => {
  const variant = (props.variant as string) || "idea";
  const isScience = variant === "science";
  const label = (props.label as string) || (isScience ? "Light" : "Idea");
  const accentColor =
    (props.accentColor as string) || (isScience ? "#60a5fa" : "#f59e0b");
  const animationProgress = (props.animationProgress as number) || 0;
  const isOn = animationProgress > 0.3 || isScience;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("bulb-bg", "#1a1a2e", "#0d0d1a")}
        <radialGradient id="bulb-glow" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor={accentColor} stopOpacity={isOn ? 0.5 : 0} />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#bulb-bg)" rx="16" />
      {/* Glow */}
      <circle cx="400" cy="260" r="200" fill="url(#bulb-glow)" />
      {/* Bulb glass */}
      <path d="M340,300 Q340,160 400,140 Q460,160 460,300 Q460,340 440,360 L360,360 Q340,340 340,300Z"
        fill={isOn ? accentColor : "#2c3e50"} opacity={isOn ? 0.85 : 0.6}
        stroke={isOn ? "#fff" : "#546e7a"} strokeWidth="2" />
      {/* Filament (science bulb) */}
      {isScience && (
        <g>
          <path d="M380,240 Q390,220 400,240 Q410,260 420,240" fill="none"
            stroke={isOn ? "#e2e8f0" : "#7f8c8d"} strokeWidth="2" />
          <line x1="392" y1="240" x2="392" y2="260" stroke="#94a3b8" strokeWidth="1.5" />
          <line x1="408" y1="240" x2="408" y2="260" stroke="#94a3b8" strokeWidth="1.5" />
        </g>
      )}
      {/* Base */}
      <rect x="365" y="360" width="70" height="10" rx="2" fill="#95a5a6" />
      <rect x="370" y="370" width="60" height="8" rx="2" fill="#7f8c8d" />
      <rect x="375" y="378" width="50" height="8" rx="2" fill="#95a5a6" />
      <rect x="380" y="386" width="40" height="10" rx="4" fill="#7f8c8d" />
      {/* Rays when on (idea bulb only) */}
      {!isScience && isOn && [0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <line key={angle}
          x1={400 + Math.cos(angle * Math.PI / 180) * 130}
          y1={260 + Math.sin(angle * Math.PI / 180) * 130}
          x2={400 + Math.cos(angle * Math.PI / 180) * 160}
          y2={260 + Math.sin(angle * Math.PI / 180) * 160}
          stroke={accentColor} strokeWidth="2" opacity="0.6" strokeLinecap="round" />
      ))}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

export const IdeaBulb: React.FC<Record<string, unknown>> = (props) => (
  <LightBulb {...props} variant="idea" />
);

export const ScienceBulb: React.FC<Record<string, unknown>> = (props) => (
  <LightBulb {...props} variant="science" />
);

/* ─── 3. Gear Mechanism ───────────────────────────────────── */
export const GearMechanism: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) || 0;
  const label = (props.label as string) || "Mechanism";
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("gear-bg", "#1c2833", "#0d1117")}
        <filter id="gear-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#gear-bg)" rx="16" />
      {/* Large gear */}
      <g transform={`rotate(${rotation}, 320, 280)`} filter="url(#gear-sh)">
        <circle cx="320" cy="280" r="100" fill="#34495e" stroke={accentColor} strokeWidth="3" />
        {Array.from({ length: 12 }, (_, i) => i * 30).map((a) => (
          <rect key={a} x="310" y="170" width="20" height="30" rx="4" fill="#34495e"
            stroke={accentColor} strokeWidth="2"
            transform={`rotate(${a}, 320, 280)`} />
        ))}
        <circle cx="320" cy="280" r="25" fill="#1c2833" stroke={accentColor} strokeWidth="2" />
      </g>
      {/* Small gear (counter-rotating) */}
      <g transform={`rotate(${-rotation * 1.6}, 520, 280)`} filter="url(#gear-sh)">
        <circle cx="520" cy="280" r="62" fill="#34495e" stroke="#e74c3c" strokeWidth="3" />
        {Array.from({ length: 8 }, (_, i) => i * 45).map((a) => (
          <rect key={a} x="512" y="210" width="16" height="24" rx="3" fill="#34495e"
            stroke="#e74c3c" strokeWidth="2"
            transform={`rotate(${a}, 520, 280)`} />
        ))}
        <circle cx="520" cy="280" r="16" fill="#1c2833" stroke="#e74c3c" strokeWidth="2" />
      </g>
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Arrow Cycle ──────────────────────────────────────── */
export const ArrowCycle: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Cycle";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const steps = (props.steps as string[]) || ["Step 1", "Step 2", "Step 3", "Step 4"];
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("ac-bg", "#1a1a2e", "#0d0d1a")}
        <marker id="ac-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 Z" fill={accentColor} />
        </marker>
      </defs>
      <rect width="800" height="600" fill="url(#ac-bg)" rx="16" />
      {/* 4 curved arrows in a circle */}
      {[0, 90, 180, 270].map((angle, i) => {
        const rad = angle * Math.PI / 180;
        const cx = 400 + Math.cos(rad) * 140;
        const cy = 280 + Math.sin(rad) * 140;
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r="50"
              fill={i === activeIndex ? accentColor : "#2c3e50"}
              stroke={i <= activeIndex ? accentColor : "#546e7a"} strokeWidth="2" />
            <text x={cx} y={cy + 5} textAnchor="middle" fill="#ecf0f1" fontSize="12" fontWeight="500">
              {steps[i] || `Step ${i + 1}`}
            </text>
          </g>
        );
      })}
      {/* Connecting arrows */}
      {[
        "M455,155 Q530,200 540,280",
        "M540,335 Q530,400 455,430",
        "M345,430 Q270,400 260,335",
        "M260,225 Q270,155 345,140",
      ].map((d, i) => (
        <path key={i} d={d} fill="none" stroke={i <= activeIndex ? accentColor : "#546e7a"}
          strokeWidth="2.5" markerEnd="url(#ac-arrow)" />
      ))}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. People Group ─────────────────────────────────────── */
export const PeopleGroup: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Population";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const groups = (props.groups as string[]) || ["Group A", "Group B", "Group C"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{symGrad("pg-bg", "#1a2632", "#0d1117")}</defs>
      <rect width="800" height="600" fill="url(#pg-bg)" rx="16" />
      {/* Three groups of people */}
      {[
        { x: 180, color: "#3498db" },
        { x: 400, color: "#e74c3c" },
        { x: 620, color: "#2ecc71" },
      ].map((g, gi) => (
        <g key={gi} opacity={gi === activeIndex ? 1 : 0.6}>
          {/* 3 people in each group */}
          {[-30, 0, 30].map((ox, pi) => (
            <g key={pi} transform={`translate(${g.x + ox}, 240)`}>
              <circle r="18" fill={gi === activeIndex ? g.color : "#546e7a"} />
              <rect x="-14" y="22" width="28" height="40" rx="8"
                fill={gi === activeIndex ? g.color : "#546e7a"} opacity="0.8" />
            </g>
          ))}
          <text x={g.x} y="360" textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="500">
            {groups[gi] || `Group ${gi + 1}`}
          </text>
        </g>
      ))}
      <text x="400" y="460" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Clock Face ───────────────────────────────────────── */
export const ClockFace: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Time";
  const rotation = (props.rotation as number) || 0;
  const accentColor = (props.accentColor as string) || "#e74c3c";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("clf-bg", "#1a1a2e", "#0d0d1a")}
        <filter id="clf-sh"><feDropShadow dx="2" dy="3" stdDeviation="5" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#clf-bg)" rx="16" />
      <g filter="url(#clf-sh)">
        <circle cx="400" cy="280" r="200" fill="#2c3e50" stroke="#546e7a" strokeWidth="4" />
        <circle cx="400" cy="280" r="190" fill="#1c2833" />
        {/* Hour markers */}
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i * 30 - 90) * Math.PI / 180;
          return (
            <g key={i}>
              <line x1={400 + Math.cos(a) * 165} y1={280 + Math.sin(a) * 165}
                x2={400 + Math.cos(a) * 180} y2={280 + Math.sin(a) * 180}
                stroke="#ecf0f1" strokeWidth={i % 3 === 0 ? 3 : 1.5} strokeLinecap="round" />
              {i % 3 === 0 && (
                <text x={400 + Math.cos(a) * 148} y={280 + Math.sin(a) * 148 + 5}
                  textAnchor="middle" fill="#ecf0f1" fontSize="16" fontWeight="600">
                  {i === 0 ? 12 : i}
                </text>
              )}
            </g>
          );
        })}
        {/* Hour hand */}
        <line x1="400" y1="280" x2={400 + Math.cos((rotation / 12 - 90) * Math.PI / 180) * 100}
          y2={280 + Math.sin((rotation / 12 - 90) * Math.PI / 180) * 100}
          stroke="#ecf0f1" strokeWidth="5" strokeLinecap="round" />
        {/* Minute hand */}
        <line x1="400" y1="280" x2={400 + Math.cos((rotation - 90) * Math.PI / 180) * 140}
          y2={280 + Math.sin((rotation - 90) * Math.PI / 180) * 140}
          stroke="#bdc3c7" strokeWidth="3" strokeLinecap="round" />
        {/* Second hand */}
        <line x1="400" y1="280" x2={400 + Math.cos((rotation * 12 - 90) * Math.PI / 180) * 150}
          y2={280 + Math.sin((rotation * 12 - 90) * Math.PI / 180) * 150}
          stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="400" cy="280" r="6" fill={accentColor} />
      </g>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Shield Badge ─────────────────────────────────────── */
export const ShieldBadge: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Security";
  const accentColor = (props.accentColor as string) || "#2ecc71";
  const fillPercent = (props.fillPercent as number) ?? 100;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("sb-bg", "#1a2632", "#0d1117")}
        <clipPath id="sb-clip">
          <path d="M400,80 L540,140 L540,320 Q540,440 400,500 Q260,440 260,320 L260,140Z" />
        </clipPath>
        <filter id="sb-glow"><feDropShadow dx="0" dy="3" stdDeviation="8" floodColor={accentColor} floodOpacity="0.35" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#sb-bg)" rx="16" />
      <g filter="url(#sb-glow)">
        {/* Shield shape */}
        <path d="M400,80 L540,140 L540,320 Q540,440 400,500 Q260,440 260,320 L260,140Z"
          fill="#2c3e50" stroke={accentColor} strokeWidth="3" />
        {/* Fill level */}
        <rect x="260" y={500 - fillPercent * 4.2} width="280" height={fillPercent * 4.2}
          fill={accentColor} opacity="0.25" clipPath="url(#sb-clip)" />
        {/* Checkmark */}
        <path d="M350,290 L385,330 L460,240" fill="none" stroke={accentColor} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Magnifying Glass ─────────────────────────────────── */
export const MagnifyingGlass: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Search";
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("mg-bg", "#1a1a2e", "#0d0d1a")}
        <radialGradient id="mg-lens" cx="40%" cy="35%" r="55%">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0.03" />
        </radialGradient>
        <filter id="mg-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#mg-bg)" rx="16" />
      <g filter="url(#mg-sh)">
        {/* Lens */}
        <circle cx="360" cy="240" r="140" fill="url(#mg-lens)" stroke={accentColor} strokeWidth="6" />
        {/* Shine */}
        <path d="M290,180 Q310,160 340,170" fill="none" stroke="#fff" strokeWidth="3" opacity="0.4" strokeLinecap="round" />
        {/* Handle */}
        <line x1="460" y1="350" x2="580" y2="470" stroke="#7f8c8d" strokeWidth="18" strokeLinecap="round" />
        <line x1="460" y1="350" x2="580" y2="470" stroke="#95a5a6" strokeWidth="12" strokeLinecap="round" />
      </g>
      <text x="400" y="545" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 9. Target Bullseye ──────────────────────────────────── */
export const TargetBullseye: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Goal";
  const accentColor = (props.accentColor as string) || "#e74c3c";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{symGrad("tb-bg", "#1a1a2e", "#0d0d1a")}</defs>
      <rect width="800" height="600" fill="url(#tb-bg)" rx="16" />
      {/* Rings */}
      {[180, 140, 100, 60, 25].map((r, i) => (
        <circle key={i} cx="400" cy="280" r={r}
          fill={i % 2 === 0 ? accentColor : "#ecf0f1"}
          opacity={i === activeIndex ? 1 : (i % 2 === 0 ? 0.7 : 0.15)}
          stroke={i === activeIndex ? "#fff" : "none"} strokeWidth="3" />
      ))}
      {/* Arrow */}
      <line x1="540" y1="140" x2="410" y2="270" stroke="#f39c12" strokeWidth="3" />
      <polygon points="400,280 415,260 425,270" fill="#f39c12" />
      {/* Feathers */}
      <line x1="540" y1="140" x2="560" y2="120" stroke="#bdc3c7" strokeWidth="2" />
      <line x1="540" y1="140" x2="555" y2="145" stroke="#bdc3c7" strokeWidth="2" />
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 10. Key Lock ────────────────────────────────────────── */
export const KeyLock: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Security";
  const accentColor = (props.accentColor as string) || "#f39c12";
  const animationProgress = (props.animationProgress as number) || 0;
  const isUnlocked = animationProgress > 0.5;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("kl-bg", "#1a2632", "#0d1117")}
        <filter id="kl-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#kl-bg)" rx="16" />
      <g filter="url(#kl-sh)">
        {/* Lock body */}
        <rect x="330" y="260" width="140" height="120" rx="12" fill="#34495e" stroke="#546e7a" strokeWidth="2" />
        {/* Shackle */}
        <path d={isUnlocked
          ? "M360,260 L360,200 Q360,150 400,150 Q440,150 440,200 L440,220"
          : "M360,260 L360,200 Q360,150 400,150 Q440,150 440,200 L440,260"}
          fill="none" stroke={accentColor} strokeWidth="12" strokeLinecap="round" />
        {/* Keyhole */}
        <circle cx="400" cy="310" r="16" fill="#1c2833" />
        <rect x="394" y="310" width="12" height="30" rx="3" fill="#1c2833" />
        {/* Key */}
        <g transform={`translate(${isUnlocked ? 520 : 400}, ${isUnlocked ? 320 : 320}) rotate(${isUnlocked ? 0 : 90})`}>
          <circle r="14" fill={accentColor} stroke="#fff" strokeWidth="1.5" />
          <rect x="14" y="-4" width="60" height="8" rx="2" fill={accentColor} />
          <rect x="60" y="-4" width="8" height="16" rx="1" fill={accentColor} />
          <rect x="50" y="-4" width="8" height="12" rx="1" fill={accentColor} />
        </g>
      </g>
      <text x="400" y="480" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">
        {isUnlocked ? "Unlocked" : "Locked"}
      </text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 11. Flag Banner ─────────────────────────────────────── */
export const FlagBanner: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Achievement";
  const accentColor = (props.accentColor as string) || "#e74c3c";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{symGrad("fb-bg", "#1a1a2e", "#0d0d1a")}</defs>
      <rect width="800" height="600" fill="url(#fb-bg)" rx="16" />
      {/* Pole */}
      <rect x="245" y="80" width="10" height="440" rx="3" fill="#95a5a6" />
      <circle cx="250" cy="80" r="8" fill="#bdc3c7" />
      {/* Flag waving */}
      <path d={`M255,100 Q400,${80 + Math.sin(phase) * 20} 550,${110 + Math.sin(phase * 1.3) * 15}
        L550,${260 + Math.sin(phase * 0.8) * 10} Q400,${280 + Math.sin(phase * 1.1) * 18} 255,260Z`}
        fill={accentColor} opacity="0.85" />
      {/* Flag stripe */}
      <path d={`M255,160 Q400,${140 + Math.sin(phase) * 15} 550,${170 + Math.sin(phase * 1.2) * 12}
        L550,${200 + Math.sin(phase * 0.9) * 12} Q400,${220 + Math.sin(phase * 1.1) * 15} 255,200Z`}
        fill="#fff" opacity="0.2" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 12. Trophy Cup ──────────────────────────────────────── */
export const TrophyCup: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Winner";
  const accentColor = (props.accentColor as string) || "#f1c40f";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {symGrad("tc-bg", "#1a1a2e", "#0d0d1a")}
        {symGrad("tc-gold", "#f1c40f", "#d4ac0d")}
        <filter id="tc-glow"><feDropShadow dx="0" dy="3" stdDeviation="8" floodColor={accentColor} floodOpacity="0.35" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#tc-bg)" rx="16" />
      <g filter="url(#tc-glow)">
        {/* Cup body */}
        <path d="M310,140 L310,280 Q310,360 400,380 Q490,360 490,280 L490,140Z"
          fill="url(#tc-gold)" stroke="#d4ac0d" strokeWidth="2" />
        {/* Handles */}
        <path d="M310,170 Q240,170 240,230 Q240,290 310,290" fill="none" stroke="url(#tc-gold)" strokeWidth="10" />
        <path d="M490,170 Q560,170 560,230 Q560,290 490,290" fill="none" stroke="url(#tc-gold)" strokeWidth="10" />
        {/* Stem */}
        <rect x="385" y="380" width="30" height="50" rx="4" fill="#d4ac0d" />
        {/* Base */}
        <ellipse cx="400" cy="440" rx="70" ry="12" fill="#d4ac0d" />
        <rect x="340" y="440" width="120" height="15" rx="4" fill="#c9960c" />
        {/* Star */}
        <polygon points="400,180 412,215 450,215 420,240 430,275 400,255 370,275 380,240 350,215 388,215"
          fill="#fff" opacity="0.4" />
        {/* Shine */}
        <path d="M330,160 Q340,150 355,155" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.5" strokeLinecap="round" />
      </g>
      <text x="400" y="510" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};
